import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark community exterior',
    },
  },
};

export function NewsHero() {
  const bg = useSlotGroup('newshero.background', bgDef);

  return (
    <section className="relative flex min-h-[min(62svh,560px)] w-full flex-col justify-end overflow-clip bg-[rgb(var(--ala-dark))] md:min-h-[560px]">
      <EditableSlotGroup groupPath="newshero.background" definition={bgDef}>
        <div className="absolute inset-0 z-0">
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            priority
            sizes="100vw"
            className="h-full w-full object-cover"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(100deg, rgb(var(--ala-primary)) 0%, rgb(var(--ala-primary)/0.72) 48%, rgb(var(--ala-primary)/0.35) 100%)',
            }}
          />
        </div>
      </EditableSlotGroup>

      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="relative z-10 flex max-w-[820px] flex-col gap-4 p-6 pb-12 md:pb-[clamp(28px,5vw,72px)] md:pl-[clamp(16px,6vw,87px)]"
      >
        <div className="flex items-center gap-3">
          <span className="h-[9px] min-h-[9px] w-[9px] rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
          <RichTextContent
            path="newshero.eyebrow"
            label="Eyebrow"
            as="span"
            className="font-heading text-base font-bold uppercase leading-7 tracking-[2px] text-[rgb(var(--ala-on-primary))]"
          />
        </div>
        <RichTextContent
          path="newshero.title"
          label="Title"
          as="h1"
          className="font-heading text-[clamp(38px,4.86vw,70px)] font-bold leading-[1.1] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
        />
        <RichTextContent
          path="newshero.subtitle"
          label="Subtitle"
          as="div"
          className="max-w-[42ch] font-heading text-base font-normal leading-relaxed text-[rgb(var(--ala-on-primary))]/75 [&_p]:mb-0"
        />
      </motion.div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NewsHero',
  displayName: 'News Hero',
  tokens: {
    'newshero.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>News &amp; Events</p>',
    },
    'newshero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Company news and announcements</p>',
    },
    'newshero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Press releases, acquisitions, and stories from across the American Landmark platform.</p>',
    },
  },
  slotGroups: {
    'newshero.background': bgDef,
  },
};

(NewsHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NewsHero;
